import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiBox, FiBriefcase, FiList, FiArrowRight, FiActivity } from 'react-icons/fi';
import toast from 'react-hot-toast';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export default function TechDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/product/getAllProducts');
      setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []));
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto pb-12 bg-blue-50/50 p-6 rounded-3xl border border-blue-100">
      
      {/* Header section - clean & minimal */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-100 pb-6 mb-8">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <FiActivity className="text-blue-900" /> Tech Dashboard
          </h2>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            Overview of active products, projects, and pending tasks.
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => navigate('/EmpProducts')}
            className="flex items-center gap-2 bg-white hover:bg-blue-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <FiBox className="text-blue-900" /> Products
          </button>
          <button 
            onClick={() => navigate('/ProjectDashboard')}
            className="flex items-center gap-2 bg-white hover:bg-blue-50 border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <FiBriefcase className="text-blue-900" /> Projects
          </button>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div 
          className="bg-white p-6 rounded-2xl shadow-sm border border-transparent hover:border-blue-200 hover:shadow-md transition-all cursor-pointer flex flex-col"
          onClick={() => navigate('/EmpProducts')}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-slate-500">Active Products</span>
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-900">
              <FiBox size={14} />
            </div>
          </div>
          <div className="text-3xl font-bold text-slate-900">{loading ? '-' : products.length}</div>
        </div>
        
        <div 
          className="bg-white p-6 rounded-2xl shadow-sm border border-transparent hover:border-blue-200 hover:shadow-md transition-all cursor-pointer flex flex-col"
          onClick={() => navigate('/ProjectDashboard')}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-slate-500">Projects Directory</span>
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-900">
              <FiBriefcase size={14} />
            </div>
          </div>
          <div className="text-sm font-medium text-slate-500 mt-auto flex items-center gap-1 hover:text-blue-900 transition-colors">
            View all projects <FiArrowRight size={12} />
          </div>
        </div>
        
        <div 
          className="bg-white p-6 rounded-2xl shadow-sm border border-transparent hover:border-blue-200 hover:shadow-md transition-all cursor-pointer flex flex-col"
          onClick={() => navigate('/TasksView')}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-slate-500">My Tasks</span>
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-900">
              <FiList size={14} />
            </div>
          </div>
          <div className="text-sm font-medium text-slate-500 mt-auto flex items-center gap-1 hover:text-blue-900 transition-colors">
            View pending assignments <FiArrowRight size={12} />
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="bg-white rounded-2xl shadow-sm border border-transparent overflow-hidden">
        <div className="px-6 py-5 border-b border-blue-50 flex justify-between items-center bg-white">
          <h3 className="text-sm font-bold text-black flex items-center gap-2">
            <div className="w-2 h-4 bg-blue-500 rounded-full" />
            Products Overview
          </h3>
          <button className="text-xs font-semibold text-blue-900 hover:text-blue-700 flex items-center gap-1 transition-colors" onClick={() => navigate('/EmpProducts')}>
            View All <FiArrowRight size={12} />
          </button>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-500">No products found.</p>
          </div>
        ) : (
          <div className="divide-y divide-blue-50">
            {products.slice(0, 5).map(product => (
              <div key={product.id} className="p-4 sm:px-6 hover:bg-blue-50/50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer group" onClick={() => navigate(`/EmpProducts/${product.id}`)}>
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-black font-bold text-sm shrink-0">
                    {product.productname?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col justify-center h-10">
                    <h4 className="font-semibold text-black text-sm truncate group-hover:text-blue-700 transition-colors">{product.productname}</h4>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{product.description || 'No description provided'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider ${
                    product.status === 'ACTIVE' ? 'bg-blue-50 text-blue-900' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {product.status || 'UNKNOWN'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
